/* 

Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained
access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.

*/

import { useState, useEffect } from 'react';
import DefaultModal from './DefaultModal';
import { Button } from '@mui/material';
import Style from './../../css/LoanHistory.module.css';

type ReverseModalProps = {
  open: boolean;
  onClose: any;
  reverseTransaction: any;
  batches: string[];
};

enum ReverseTransaction {
  NSF_REVERSE = '03 - Reversal due to NSF',
  MIS_REVERSE = '04 - Reversal due to misapplication',
}
enum NsfDescriptions {
  NSF_FUNDS = 'Insufficient Funds',
  PMT_STOP = 'Payment Stopped',
  NOT_FOUND = 'Account Not Found',
  INV_ACCTNO = 'Invalid Account Number',
  CLOSED = 'Account Closed',
  RFR_MAKER = 'Refer to Maker',
  UNCLCT_FNDS = 'Uncollected Funds',
  NOT_TXN_ACCT = 'Non Transaction Account',
  FROZEN = 'Account Frozen',
  UNAUTHORIZED = 'Unauthorized Payment',
  BENE_DEAD = 'Beneficiary Deceased',
  AUTH_REVOKED = 'Authorization Revoked',
  OTHER = 'Other',
}

export default function ReverseModal(props: ReverseModalProps) {
  const [selectedTransaction, setSelectedTransaction] = useState('');
  const [batchNumber, setBatchNumber] = useState('');
  const [description, setDescription] = useState('');
  const [confirmDisabled, setConfirmDisabled] = useState(true);
  const [open, setOpen] = useState(false);

  const handleClose = () => {
    setOpen(false);
  };

  const handleOpen = () => {
    setOpen(true);
  };

  const handleChangeTransaction = (event: any) => {
    setSelectedTransaction(event.target.value);
    setDescription('');
  };

  const submitTransaction = () => {
    props.reverseTransaction(selectedTransaction.split('-')[0]?.trim(), batchNumber, description);
    handleClose();
    props.onClose();
  };

  const setBatchValue = (value: string) => {
    if (value) {
      setBatchNumber(value);
    } else {
      setBatchNumber('');
    }
  };

  useEffect(() => {
    if (selectedTransaction !== '' && batchNumber !== '') {
      setConfirmDisabled(false);
    } else {
      setConfirmDisabled(true);
    }
  }, [selectedTransaction, batchNumber]);

  return (
    <DefaultModal
      open={props.open}
      onClose={props.onClose}
      title={<h1>Confirm Reverse Transaction</h1>}
      content={
        <div className={Style.content}>
          <p>
            Reminder: For transactions that alter Principal you must reverse them in reverse order from the way they
            were orginally submitted.
          </p>
          <div className={Style.footer}>
            <select
              value={selectedTransaction}
              onChange={(event) => handleChangeTransaction(event)}
              className={Style.select}
            >
              <option disabled value=''>
                {' '}
                -- select an option --{' '}
              </option>
              {Object.values(ReverseTransaction).map((value: string, index: number) => (
                <option key={index} value={value}>
                  {value}
                </option>
              ))}
            </select>
            <select
              value={batchNumber}
              onChange={(event) => setBatchValue(event.target.value)}
              className={Style.select}
            >
              <option disabled value=''>
                {' '}
                -- select an option --{' '}
              </option>
              {props.batches.map((value: string, index: number) => (
                <option key={index} value={value}>
                  {value}
                </option>
              ))}
            </select>
            <select
              value={description}
              disabled={selectedTransaction.split('-')[0]?.trim() !== '03'}
              onChange={(event) => setDescription(event.target.value)}
              className={Style.select}
            >
              <option disabled value=''>
                {' '}
                -- select an option --{' '}
              </option>
              {Object.values(NsfDescriptions).map((value: string, index: number) => (
                <option key={index} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </div>
        </div>
      }
      footer={
        <div className={Style.footer}>
          <Button variant='outlined' onClick={props.onClose} className={Style.cancelButton}>
            Cancel
          </Button>
          <Button variant='contained' className={Style.button} onClick={handleOpen} disabled={confirmDisabled}>
            Confirm
          </Button>
          <DefaultModal
            open={open}
            onClose={handleClose}
            title={<h1>Are You Sure?</h1>}
            content={<p className={Style.content}>Are you sure you want to reverse the selected Transaction?</p>}
            footer={
              <div className={Style.footer}>
                <Button autoFocus onClick={handleClose} className={Style.cancelButton}>
                  No
                </Button>
                <Button onClick={submitTransaction} autoFocus className={Style.button}>
                  Yes
                </Button>
              </div>
            }
          />
        </div>
      }
    />
  );
}
